/**
 * מפת העולם — Travian style
 * ================================================================
 * SVG interactive.
 * 65 ערים על עולם, כל עיר מצוירת כפין (עגול).
 * צבע = מי שולט (חצר של השחקן = זהב, AI = לפי אתניות)
 * לחיצה על עיר → פאנל צד עם פרטים ופעולות
 */

import { useState } from 'react';
import type { RoundPlayer } from '../../api/client.js';
import type { WorldState, Dyn } from '../RoundScreen.js';
import { CityPanel } from './CityPanel.js';

// אזורי המפה (רקע ויזואלי מקדמי)
const REGIONS: Array<{
  id: string;
  name: string;
  x: number;
  y: number;
  fill: string;
}> = [
  { id: 'eastern_europe', name: 'אירופה מזרחית', x: 590, y: 210, fill: 'rgba(122, 46, 31, 0.05)' },
  { id: 'western_europe', name: 'אירופה מערבית', x: 465, y: 200, fill: 'rgba(74, 107, 138, 0.05)' },
  { id: 'north_america', name: 'צפון אמריקה', x: 200, y: 230, fill: 'rgba(74, 107, 58, 0.05)' },
  { id: 'israel', name: 'ישראל', x: 623, y: 315, fill: 'rgba(184, 150, 106, 0.08)' },
  { id: 'mediterranean_africa', name: 'ים תיכון', x: 490, y: 320, fill: 'rgba(184, 128, 26, 0.05)' },
  { id: 'middle_east', name: 'מזרח תיכון', x: 680, y: 340, fill: 'rgba(139, 35, 35, 0.05)' },
  { id: 'latin_america', name: 'אמריקה הלטינית', x: 240, y: 430, fill: 'rgba(163, 124, 196, 0.05)' },
];

// מפרשות המפה
const MAP_WIDTH = 900;
const MAP_HEIGHT = 550;

export function MapView({
  state,
  player,
  selectedCityId,
  onSelectCity,
  onIntent,
}: {
  state: WorldState;
  player: RoundPlayer;
  selectedCityId: string | null;
  onSelectCity: (id: string | null) => void;
  onIntent: (type: string, payload: Record<string, unknown>) => Promise<{ success: boolean; message?: string }>;
}) {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [hoveredCity, setHoveredCity] = useState<string | null>(null);

  const cities = Object.values(state.entities.cities);
  const dynasties = Object.values(state.entities.dynasties);
  const playerDynasty = state.entities.dynasties[player.dynasty_id];

  // מפה: cityId → dynasty ששולטת (מי שיש לו הכי הרבה חסידים בעיר)
  const cityRulers = new Map<string, { dynasty: Dyn; count: number }>();
  for (const f of state.followers) {
    const existing = cityRulers.get(f.cityId);
    if (!existing || f.count > existing.count) {
      const d = dynasties.find((x) => x.id === f.dynastyId);
      if (d) cityRulers.set(f.cityId, { dynasty: d, count: f.count });
    }
  }

  const selectedCity = selectedCityId ? state.entities.cities[selectedCityId] : null;

  return (
    <div className="map-view">
      <div className="map-container">
        <div className="map-controls">
          <button onClick={() => setZoom((z) => Math.min(2.5, z + 0.2))}>➕</button>
          <button onClick={() => setZoom((z) => Math.max(0.5, z - 0.2))}>➖</button>
          <button onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}>🎯</button>
          <div className="map-legend">
            <span><span className="dot player" /> החצר שלי</span>
            <span><span className="dot ashkenazi" /> אשכנזי</span>
            <span><span className="dot sephardi" /> ספרדי</span>
            <span><span className="dot empty" /> ריק</span>
          </div>
        </div>

        <svg
          viewBox={`0 0 ${MAP_WIDTH} ${MAP_HEIGHT}`}
          className="map-svg"
          preserveAspectRatio="xMidYMid meet"
        >
          <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
            {/* רקע — grid קל */}
            <defs>
              <pattern id="grid" width="30" height="30" patternUnits="userSpaceOnUse">
                <path d="M 30 0 L 0 0 0 30" fill="none" stroke="rgba(184, 150, 106, 0.1)" strokeWidth="0.5" />
              </pattern>
              <radialGradient id="parchment">
                <stop offset="0%" stopColor="#f4efe4" />
                <stop offset="100%" stopColor="#e0d0b0" />
              </radialGradient>
            </defs>
            <rect x="0" y="0" width={MAP_WIDTH} height={MAP_HEIGHT} fill="url(#parchment)" />
            <rect x="0" y="0" width={MAP_WIDTH} height={MAP_HEIGHT} fill="url(#grid)" />

            {/* אזורים — טקסט רקע */}
            {REGIONS.map((r) => (
              <g key={r.id}>
                <text
                  x={r.x}
                  y={r.y}
                  fontFamily="Frank Ruhl Libre, serif"
                  fontSize="16"
                  fill="rgba(122, 46, 31, 0.2)"
                  textAnchor="middle"
                  fontWeight="700"
                >
                  {r.name}
                </text>
              </g>
            ))}

            {/* ערים */}
            {cities.map((city) => {
              const ruler = cityRulers.get(city.id);
              const isPlayerCity = ruler?.dynasty.id === player.dynasty_id;
              const isSelected = city.id === selectedCityId;
              const isHovered = city.id === hoveredCity;
              const rulerEth = ruler?.dynasty.ethnicity ?? 'empty';

              const size = getSize(city.sizeTier) * (isSelected ? 1.4 : isHovered ? 1.2 : 1);
              const fill = isPlayerCity
                ? '#c9a961'
                : ruler
                ? getEthnicityColor(rulerEth)
                : 'rgba(122, 46, 31, 0.15)';
              const stroke = isPlayerCity
                ? '#7a2e1f'
                : isSelected
                ? '#7a2e1f'
                : 'rgba(42, 31, 20, 0.4)';

              return (
                <g
                  key={city.id}
                  onClick={() => onSelectCity(city.id)}
                  onMouseEnter={() => setHoveredCity(city.id)}
                  onMouseLeave={() => setHoveredCity(null)}
                  style={{ cursor: 'pointer' }}
                >
                  {city.isHolyPlace && (
                    <circle cx={city.x} cy={city.y} r={size + 4} fill="none" stroke="#b8801a" strokeWidth="1" opacity="0.6" />
                  )}
                  {city.hasChareidiMunicipality && (
                    <circle cx={city.x} cy={city.y} r={size + 7} fill="none" stroke="#7a2e1f" strokeWidth="1" strokeDasharray="2 2" />
                  )}
                  <circle
                    cx={city.x}
                    cy={city.y}
                    r={size}
                    fill={fill}
                    stroke={stroke}
                    strokeWidth={isSelected ? 3 : 1.5}
                  />
                  {(isHovered || isSelected || isPlayerCity) && (
                    <text
                      x={city.x}
                      y={city.y - size - 4}
                      textAnchor="middle"
                      fontSize="9"
                      fontFamily="Frank Ruhl Libre, serif"
                      fontWeight="600"
                      fill="#2a1f14"
                    >
                      {city.name}
                    </text>
                  )}
                  {ruler && (isHovered || isSelected) && (
                    <text
                      x={city.x}
                      y={city.y + size + 10}
                      textAnchor="middle"
                      fontSize="7"
                      fill="#6b5842"
                    >
                      {ruler.dynasty.name}
                    </text>
                  )}
                </g>
              );
            })}
          </g>
        </svg>
      </div>

      {selectedCity && (
        <CityPanel
          city={selectedCity}
          state={state}
          player={player}
          playerDynasty={playerDynasty}
          onClose={() => onSelectCity(null)}
          onIntent={onIntent}
        />
      )}
    </div>
  );
}

function getSize(tier: string): number {
  switch (tier) {
    case 'metropolis':
      return 8;
    case 'city':
      return 6;
    case 'town':
      return 4;
    case 'village':
      return 3;
    default:
      return 4;
  }
}

function getEthnicityColor(eth: string): string {
  switch (eth) {
    case 'ashkenazi':
      return '#4a5c8f'; // כחול
    case 'sephardi':
      return '#8b4a2b'; // חום
    case 'mizrahi':
      return '#a37c4c'; // חום בהיר
    case 'yemenite':
      return '#7a3e5f'; // סגול-חום
    default:
      return '#9b8869';
  }
}
